/* eslint-disable import/order */
import { createApp } from 'vue'
import { createPinia } from 'pinia'

// eslint-disable-next-line no-unused-vars
import i18n from './i18n/i18n.js'
import App from './App.vue'
import { createRouter } from './router.js'
import { Vue3Mq } from 'vue3-mq'
import VueGtag from 'vue-gtag'
import VueConfetti from 'vue-confetti'
import FloatingVue from 'floating-vue'
import VueClipboard from 'vue3-clipboard'
import { createGtm } from '@gtm-support/vue-gtm'
import VueApexCharts from 'vue3-apexcharts'
import { createPopper } from '@popperjs/core'

import 'bootstrap'

// plugins
import VueCan from '@/plugins/vue-can.js'

import * as Sentry from '@sentry/vue'

import { eventEmitter } from './event-bus.js'
import { clarityService } from './services/clarity.js'

import VueImageZoomer from 'vue-image-zoomer'

const app = createApp(App)
const pinia = createPinia()

// scripts
import '@/utilities/prototype.js'

// styles
import 'vue-multiselect/dist/vue-multiselect.css'
import 'nprogress/nprogress.css'
import 'vue-image-zoomer/dist/style.css'
import 'floating-vue/dist/style.css'
import 'bootstrap-float-label/dist/bootstrap-float-label.min.css'
import '@/assets/css/main.css'

import utilities from '@/utilities/utilities.js'
import '@/utilities/echo.js'

import { filters } from '@/filters/index.js'

import clickOutside from '@/directives/click-outside.js'
import preventNumberScroll from '@/directives/prevent-number-scroll.js'
import StickyScrollDirective from '@/directives/sticky-on-scroll.js'
import autoHideDirective from '@/directives/auto-hide-directive.js'
import firebase from './plugins/firebase.js'

app.config.globalProperties.$createPopper = createPopper

// directives
app.directive('click-outside', clickOutside)
app.directive('prevent-number-scroll', preventNumberScroll)
app.directive('auto-hide', autoHideDirective)
app.directive('sticky-on-scroll', StickyScrollDirective)

// globalProperties register
Object.entries(utilities).forEach(item => {
  app.config.globalProperties[`$${item[0]}`] = item[1]
})

app.config.globalProperties.$filters = filters

app.config.globalProperties.eventEmitter = eventEmitter

const router = createRouter(app)
if (import.meta.env.MODE === 'production') {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        mask: ['.form-control', '.multiselect'],
        maskAllText: false,
        blockAllMedia: false,

        networkDetailAllowUrls: [/\/v1\/(retailer).*/],

        beforeErrorSampling: event => {
          if (!window.location.pathname.startsWith('/retailer/application/')) {
            return false
          }

          return true
        }
      })
    ],
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.0,

    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', 'tarfin.com', /^\//]
  })
  Sentry.setTag('locale', import.meta.env.VITE_I18N_LOCALE)
  Sentry.setTag('env', import.meta.env.MODE)
  Sentry.setTag('country', import.meta.env.VITE_COUNTRY)
}

const shouldTrack =
  window.location.pathname.split('/')[1] !== 'admin' &&
  (import.meta.env.MODE === 'production' || import.meta.env.MODE === 'staging')

app
  .use(router)
  .use(pinia)
  .use(Vue3Mq, {
    preset: 'bootstrap4',
    breakpoints: {
      xs: 0,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1280
    }
  })

if (shouldTrack) {
  app
    .use(VueGtag, {
      config: { id: 'UA-70760483-1' } // Do not change this ID without notifying the Product team
    })
    .use(
      createGtm({
        id: 'GTM-TBC5RGQ' // Do not change this ID without notifying the Product team
      })
    )
}

app
  .use(VueApexCharts)
  .use(VueImageZoomer)
  .use(VueCan)
  .use(VueClipboard, {
    autoSetContainer: true,
    appendToBody: true
  })
  .use(VueConfetti)
  .use(FloatingVue)
  .use(i18n)
  .use(firebase)
  .mount('#app')

// Initialize Clarity after window load to ensure all resources (CSS) are ready
if (shouldTrack) {
  const startClarity = () => {
    if (!clarityService.initialized) {
      clarityService.initialize()
    }
  }

  if (document.readyState === 'complete') {
    startClarity()
  } else {
    window.addEventListener('load', startClarity)
  }
}
